import {
  ADD_TO_COMPARE_LIMIT,
  CompareMetadata,
  CompareTableParams,
  entriesToCompareMetadata,
  entriesToIdsAndNames,
} from '@neptune/compare-domain';
import {
  SearchCriterion,
  SearchQuery,
  SearchQueryModelConverter,
} from '@neptune/search-query-domain';
import {
  LeaderboardEntry,
  LeaderboardQueryNameAliases,
  searchLeaderboard,
  SortOptionsModelConverter,
} from '@neptune/shared/leaderboard-domain';

import {
  KnownAttributes,
  knownAttributesForExperimentIdentification,
} from 'domain/experiment/attribute';

const EXPERIMENT_HEAD_CRITERION: SearchCriterion = {
  value: true,
  type: 'bool',
  operator: '=',
  attribute: KnownAttributes.ExperimentIsHead,
} as const;

export type FilteredCompareResult<
  T extends string[] | Set<string> = string[] | Set<string>,
  R = T extends string[] ? string[] : Set<string>,
> = {
  metadata: CompareMetadata;
  identificationKeys: { runIds: R; experimentNames: R };
};

export async function resolveFilteredCompareEntries(
  projectIdentifier: string,
  runIds: string[],
  experimentNames: string[],
  tableParams: CompareTableParams,
  queryNameAliases?: LeaderboardQueryNameAliases,
  showSelectedHiddenByFilter?: boolean,
): Promise<LeaderboardEntry[]> {
  let query = undefined;
  const keysCriteria = [
    ...getKeysCriteria(runIds, KnownAttributes.Id),
    ...getKeysCriteria(experimentNames, KnownAttributes.Name),
  ];

  if (!showSelectedHiddenByFilter) {
    query = SearchQueryModelConverter.convertSearchQueryToNql({
      criteria: [
        { operator: 'or', criteria: keysCriteria },
        SearchQueryModelConverter.convertNqlToSearchQuery(tableParams.query),
      ],
      operator: 'and',
    });
  } else {
    query = SearchQueryModelConverter.convertSearchQueryToNql({
      criteria: [{ operator: 'or', criteria: keysCriteria }],
      operator: 'and',
    });
  }

  const leaderboardResult = await searchLeaderboard({
    type: ['run'],
    projectIdentifier,
    experimentsOnly: tableParams.experimentsOnly,
    queryNameAliases,
    pagination: {
      limit: ADD_TO_COMPARE_LIMIT,
      offset: 0,
    },
    query,
    attributesToFetch: knownAttributesForExperimentIdentification,
    sorting: tableParams.sortOptions
      ? SortOptionsModelConverter.fromLegacyToDomain(tableParams.sortOptions)
      : undefined,
  });

  return leaderboardResult.entries;
}

export async function resolveFilteredCompareRunIdsAndExperimentNames(
  projectIdentifier: string,
  runIds: string[],
  experimentNames: string[],
  tableParams: CompareTableParams,
  queryNameAliases?: LeaderboardQueryNameAliases,
  showSelectedHiddenByFilter?: boolean,
): Promise<FilteredCompareResult<Set<string>>> {
  if (!runIds.length && !experimentNames.length) {
    return {
      metadata: new Map(),
      identificationKeys: { runIds: new Set(), experimentNames: new Set() },
    };
  }

  const entries = await resolveFilteredCompareEntries(
    projectIdentifier,
    runIds,
    experimentNames,
    tableParams,
    queryNameAliases,
    showSelectedHiddenByFilter,
  );

  const metadata = entriesToCompareMetadata(entries);
  const identificationKeys = entriesToIdsAndNames(entries);

  return { metadata, identificationKeys };
}

export async function resolveFilteredCompareRunIdsAndExperimentNamesArrays(
  projectIdentifier: string,
  runIds: string[],
  experimentNames: string[],
  tableParams: CompareTableParams,
  queryNameAliases?: LeaderboardQueryNameAliases,
  showSelectedHiddenByFilter?: boolean,
): Promise<FilteredCompareResult<string[]>> {
  if (!runIds.length && !experimentNames.length) {
    return { metadata: new Map(), identificationKeys: { runIds: [], experimentNames: [] } };
  }

  const entries = await resolveFilteredCompareEntries(
    projectIdentifier,
    runIds,
    experimentNames,
    tableParams,
    queryNameAliases,
    showSelectedHiddenByFilter,
  );

  const metadata = entriesToCompareMetadata(entries);
  const identificationKeys = entriesToIdsAndNames(entries, Array.from);

  return { metadata, identificationKeys };
}

function getKeysCriteria(
  entries: string[],
  attribute: KnownAttributes.Id | KnownAttributes.Name,
): Array<SearchQuery | SearchCriterion> {
  const criteria: SearchCriterion[] = entries.map((value: string) => ({
    type: 'string',
    operator: '=',
    attribute,
    value,
  }));

  if (attribute === KnownAttributes.Name) {
    return criteria.map((criterion) => ({
      criteria: [criterion, EXPERIMENT_HEAD_CRITERION],
      operator: 'and',
    }));
  }

  return criteria;
}
